/**
 * CLE-2792 — "did a person actually edit this field?", read from react-hook-form's dirty state.
 *
 * 🚨 `dirtyFields.<name>` IS NOT A BOOLEAN FOR A FIELD ARRAY, AND IT IS TRUTHY WHEN NOTHING CHANGED.
 * react-hook-form reports a field array as a parallel array of per-field flags, so
 * `Boolean(dirtyFields.benefitSelectionRules)` answers "has this array ever been written through"
 * rather than "is it different from what was loaded" — a `replace` with identical content leaves an
 * array of all-`false` entries behind, which would read as an edit. Measured on 7.43.9:
 *
 *   untouched              undefined
 *   added / removed        [{ payerIds: [true], … }, …]
 *   edited one field       [{ payerIds: [false], matchKeywords: [true], … }]
 *   reordered              [{ … true … }, { … true … }]
 *   replaced, same content [{ payerIds: [false], matchKeywords: [false], … }]   ← NOT an edit
 *
 * So the answer is whether any leaf is `true`, at any depth. Being wrong in the "false" direction
 * loses somebody's save; being wrong in the "true" direction re-sends a list unchanged, which is
 * what this code did for everyone before. Both matter, which is why the identical-content row above
 * is a test and not a comment.
 */
export const wasFieldEdited = (dirtyState: unknown): boolean => {
  if (Array.isArray(dirtyState)) return dirtyState.some(wasFieldEdited);
  if (dirtyState && typeof dirtyState === "object")
    return Object.values(dirtyState).some(wasFieldEdited);
  return dirtyState === true;
};
